import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Card,
  message,
  Spin,
  Upload,
  Row,
  Col,
  Image,
  Popconfirm,
  Divider,
  Tag,
  Typography,
} from "antd";
import {
  GET_LOAD_IMAGE_NEW_ALL,
  GET_LOAD_IMAGE_NEW_BY,
  HEADERS_EXPORT_BEN,
  HEADERS_UPIMAGE,
  POST_CONFIRM_CLOSE_CONTNO,
  POST_DELETE_IMAGE_NEW,
  POST_UPLOAD_IMAGE_NEW,
} from "../../../API/apiUrls";
import axios from "axios";
import Dragger from "antd/es/upload/Dragger";
import {
  InboxOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import Compressor from "compressorjs";

const { TextArea } = Input;
const { Text } = Typography;

// ── Upload Image Type ──────────────────────────────────────
const IMG_TYPE = { CUT_PAY: 13, RECEIPT: 14 };

// ── Reusable Image Grid ────────────────────────────────────
const ImageGrid = ({ files, loading, token, onDelete, readOnly = false }) => {
  if (!files.length) return null;
  return (
    <Spin spinning={loading}>
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        {files.map((e, index) => {
          const isPdf = e.path?.toLowerCase().endsWith(".pdf");
          const src = `${GET_LOAD_IMAGE_NEW_BY}/${e.path}?token=${token}`;
          return (
            <Col key={e.path || index} xs={12} sm={8} md={6}>
              <div style={{ position: "relative" }}>
                {!readOnly && (
                  <Popconfirm
                    title="ยืนยันการลบ"
                    description="ต้องการลบไฟล์นี้?"
                    onConfirm={() => onDelete(e)}
                    okText="ลบ"
                    cancelText="ยกเลิก"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        zIndex: 10,
                        borderRadius: 6,
                      }}
                    />
                  </Popconfirm>
                )}

                <Card
                  hoverable
                  style={{ borderRadius: 10, overflow: "hidden", padding: 0 }}
                  styles={{ body: { padding: 0 } }}
                >
                  {isPdf ? (
                    <div
                      onClick={() => window.open(src, "_blank")}
                      style={{
                        height: 120,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        background: "#fff1f0",
                        gap: 6,
                      }}
                    >
                      <FilePdfOutlined
                        style={{ fontSize: 36, color: "#ff4d4f" }}
                      />
                      <Text style={{ fontSize: 11, color: "#cf1322" }}>
                        คลิกเพื่อเปิด PDF
                      </Text>
                    </div>
                  ) : (
                    <Image
                      width="100%"
                      height={120}
                      src={src}
                      style={{ objectFit: "cover", display: "block" }}
                      preview={{ src }}
                    />
                  )}
                </Card>
              </div>
            </Col>
          );
        })}
      </Row>
    </Spin>
  );
};

// ── Upload Section ─────────────────────────────────────────
const UploadSection = ({ label, IconComponent, color, uploadProps }) => (
  <div
    style={{
      border: `1px solid ${color}22`,
      borderRadius: 12,
      padding: "16px 16px 8px",
      background: `${color}08`,
      marginBottom: 16,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconComponent style={{ color, fontSize: 16 }} /> {/* ✅ แก้ตรงนี้ */}
      </div>
      <Text strong style={{ fontSize: 14, color: "#333" }}>
        {label}
      </Text>
    </div>
    <Dragger
      {...uploadProps}
      style={{ borderRadius: 8, background: "#fafafa" }}
    >
      <InboxOutlined style={{ color, fontSize: 36 }} />
      <p style={{ margin: "8px 0 4px", fontSize: 14, color: "#555" }}>
        คลิกหรือลากไฟล์มาวางที่นี่
      </p>
      <p style={{ fontSize: 16, color: "#aaa", margin: 0 }}>
        รองรับ รูปภาพ และ PDF
      </p>
    </Dragger>
  </div>
);

const CloseContno = ({ open, close, dataDefault, funcUpdateStatus }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const token = localStorage.getItem("TOKEN");

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [allImg, setAllImg] = useState([]);
  const [fileListCutPay, setFileListCutPay] = useState([]);
  const [fileListReceipt, setFileListReceipt] = useState([]);
  const [fileListByContract, setFileListByContract] = useState([]);

  // ── Load defaults ────────────────────────────────────────
  const dataDefaultValues = useCallback(
    (data) => {
      form.setFieldsValue({ redNumber: data.redCaseNumber, memo: data.mark });
    },
    [form],
  );

  useEffect(() => {
    if (open && dataDefault) {
      dataDefaultValues(dataDefault);
      loadPath();
    }
  }, [open, dataDefault]);

  const handleCancel = () => close(false);

  // ── API ──────────────────────────────────────────────────
  const loadPath = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${GET_LOAD_IMAGE_NEW_ALL}/8/${dataDefault.loanId}`,
        { headers: HEADERS_UPIMAGE },
      );
      setFileListByContract(res.data ?? []);
    } catch {
      message.error("เกิดข้อผิดพลาดในการโหลดรูปภาพ");
      setFileListByContract([]);
    } finally {
      setLoading(false);
    }
  };

  const sendStatus = async (data) => {
    console.log("sendStatus", data);

    try {
      const res = await axios.post(POST_CONFIRM_CLOSE_CONTNO, data, {
        headers: HEADERS_EXPORT_BEN,
      });
      return res.status === 200 ? { success: true } : { success: false };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "เกิดข้อผิดพลาด",
      };
    }
  };

  const handleUpLoadIMG = useCallback(async (resultImgs, menu, data) => {
    const fd = new FormData();
    fd.append("id", data.id); //  data.id loanId
    fd.append("notis", menu);
    const sorted = resultImgs
      .filter((img) => img.originalIndex !== undefined)
      .sort((a, b) => a.type - b.type || a.originalIndex - b.originalIndex);
    for (const file of sorted) {
      fd.append("file", file.imageCompressedFile);
      fd.append("menuType", menu);
      fd.append("imageType", file.type);
    }

    console.log(resultImgs, menu);

    try {
      const res = await axios.post(POST_UPLOAD_IMAGE_NEW, fd, {
        headers: HEADERS_UPIMAGE,
      });
      return res.status === 200 ? { success: true } : { success: false };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const handleDeleteIMG = async (image) => {
    console.log(image);

    setLoading(true);
    try {
      const res = await axios.delete(POST_DELETE_IMAGE_NEW, {
        data: [image],
        headers: HEADERS_UPIMAGE,
      });
      if (res.status === 200) {
        setFileListByContract((prev) =>
          prev.filter((img) => img.path !== image.path),
        );
        message.success("ลบไฟล์สำเร็จ");
      }
    } catch {
      message.error("ลบไฟล์ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  // ── Image compress ───────────────────────────────────────
  const compressImage = async (file, type, index) => {
    return new Promise((resolve) => {
      const quality =
        file.size >= 2500000
          ? 0.1
          : file.size >= 2000000
            ? 0.15
            : file.size >= 1500000
              ? 0.2
              : file.size >= 1000000
                ? 0.3
                : 0.5;

      new Compressor(file, {
        quality,
        success: (result) => {
          const compressed = new File([result], file.name, {
            type: result.type,
            lastModified: Date.now(),
          });
          setAllImg((prev) => [
            ...prev,
            { imageCompressedFile: compressed, type, originalIndex: index },
          ]);
          setLoading(false);
          resolve(true);
        },
        error: () => {
          message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}"`);
          setLoading(false);
          resolve(true);
        },
      });
    });
  };

  const beforeUpts = async (file, type) => {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (isPdf) {
      setAllImg((prev) => [
        ...prev,
        { imageCompressedFile: file, type, originalIndex: file.uid },
      ]);
      return false;
    }
    if (file.size > 5000000) {
      message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
      return Upload.LIST_IGNORE;
    }
    setLoading(true);
    await compressImage(file, type, file.uid);
    return false;
  };

  const makeUploadProps = (fileList, setFileList, type) => ({
    name: "file",
    multiple: true,
    accept: "image/*,.pdf",
    listType: "picture",
    fileList,
    onChange: ({ fileList: newList, file }) => {
      setFileList(newList);
      if (file.status === "removed") {
        setAllImg((prev) =>
          prev.filter((img) => img.originalIndex !== file.uid),
        );
      }
    },
    beforeUpload: (file) => beforeUpts(file, type),
    iconRender: (file) =>
      file.type === "application/pdf" || file.name.endsWith(".pdf") ? (
        <FilePdfOutlined style={{ fontSize: 40, color: "#ff4d4f" }} />
      ) : null,
  });

  const onFinish = async (values) => {
    const hasCutPay = fileListCutPay.length > 0 || cutPayFiles.length > 0;
    const hasReceipt = fileListReceipt.length > 0 || receiptFiles.length > 0;

    if (!hasCutPay && !hasReceipt) {
      message.error("กรุณาอัปโหลดไฟล์ทั้ง 2 ประเภท");
      return;
    }
    if (!hasCutPay) {
      message.error("กรุณาอัปโหลดไฟล์ตัดจ่าย");
      return;
    }
    if (!hasReceipt) {
      message.error("กรุณาอัปโหลดไฟล์ใบเสร็จ");
      return;
    }

    const postStatus = {
      lawsuitId: dataDefault.lawsuitId,
      loanId: dataDefault.loanId,
      redCaseNumber: dataDefault.redCaseNumber,
      name: `${dataDefault.SNAM}${dataDefault.NAME1}${dataDefault.NAME2 || ""}`,
      CONTNO: dataDefault.CONTNO,
      idJudge: dataDefault.idJudge,
      note: values.memo,
      userId,
      LAW_TYPE_ID: dataDefault.LAW_TYPE_ID,
    };

    console.log("postStatus", postStatus);

    setLoading(true);
    try {
      const statusResult = await sendStatus(postStatus);
      if (!statusResult.success) throw new Error(statusResult.error);

      if (allImg.length > 0) {
        const uploadResult = await handleUpLoadIMG(allImg, 8, {
          id: dataDefault.loanId,
        });
        if (!uploadResult.success)
          message.warning("บันทึกสำเร็จ แต่อัปโหลดรูปล้มเหลว");
        else message.success("บันทึกรูปสำเร็จ");
      }

      await funcUpdateStatus?.();
      message.success({ content: "บันทึกสำเร็จ", duration: 2 });
      handleCancel();
    } catch (err) {
      message.error(err.message || "ไม่สามารถบันทึกข้อมูลได้");
    } finally {
      setLoading(false);
    }
  };

  const cutPayFiles = fileListByContract.filter(
    (e) => e.imageType === IMG_TYPE.CUT_PAY,
  );
  const receiptFiles = fileListByContract.filter(
    (e) => e.imageType === IMG_TYPE.RECEIPT,
  );

  const FLAG_CONFIG = {
    1: {
      background: "#f6ffed",
      border: "1px solid #b7eb8f",
      label: "ทำยอม",
      badgeColor: "#389e0d",
      badgeBg: "#d9f7be",
    },
    2: {
      background: "#fff2f0",
      border: "1px solid #ffccc7",
      label: "พิพากษา",
      badgeColor: "#cf1322",
      badgeBg: "#ffebe8",
    },
    6: {
      background: "#fff2f0",
      border: "1px solid #ffccc7",
      label: "(ส่วนต่าง)",
      badgeColor: "#cf1322",
      badgeBg: "#ffebe8",
    },
  };

  const flagStyle = FLAG_CONFIG[dataDefault.flagLoanNew] ?? FLAG_CONFIG[1];
  const lawStyle = dataDefault.LAW_TYPE_ID === 6 && FLAG_CONFIG[6];

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#e6f4ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FileProtectOutlined style={{ color: "#1677ff", fontSize: 18 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>ปิดสัญญา</div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      width={900}
      footer={null}
      styles={{ body: { padding: "0 24px 24px" } }}
    >
      <Spin spinning={loading} tip="กำลังโหลด...">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={() =>
            message.error("กรุณากรอกข้อมูลที่มีเครื่องหมาย *")
          }
          initialValues={{ memo: null }}
        >
          <Card
            size="small"
            style={{
              borderRadius: 10,
              marginBottom: 20,
              background: "#f8faff",
              border: "1px solid #e6f0ff",
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  เลขสัญญา
                </Text>
                <div>
                  <Text strong>{dataDefault?.CONTNO || "-"}</Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  เจ้าของสัญญา
                </Text>
                <div>
                  <Text strong>
                    {dataDefault?.SNAM}
                    {dataDefault?.NAME1} {dataDefault?.NAME2 || ""}
                  </Text>
                </div>
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ผลพิจารณา
                </Text>
                <div>
                  <span
                    style={{
                      background: flagStyle.badgeBg,
                      color: flagStyle.badgeColor,
                      borderRadius: 20,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {flagStyle.label}
                    {lawStyle.label}
                  </span>
                </div>
              </Col>
            </Row>
          </Card>

          <Divider
            orientation="left"
            style={{ fontSize: 13, color: "#555", margin: "8px 0 16px" }}
          >
            <FileTextOutlined /> ไฟล์แนบ
          </Divider>

          <UploadSection
            label="ไฟล์ตัดจ่าย"
            IconComponent={FileTextOutlined}
            color="#1677ff"
            uploadProps={makeUploadProps(
              fileListCutPay,
              setFileListCutPay,
              IMG_TYPE.CUT_PAY,
            )}
          />
          {cutPayFiles.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text type="secondary" style={{ fontSize: 16 }}>
                ไฟล์ตัดจ่ายที่บันทึกไว้ ({cutPayFiles.length} ไฟล์)
              </Text>
              <ImageGrid
                files={cutPayFiles}
                loading={loading}
                token={token}
                onDelete={handleDeleteIMG}
                readOnly={roleId === 1 ? false : true}
              />
            </div>
          )}
          <UploadSection
            label="ไฟล์ใบเสร็จ"
            IconComponent={FileTextOutlined}
            color="#52c41a"
            uploadProps={makeUploadProps(
              fileListReceipt,
              setFileListReceipt,
              IMG_TYPE.RECEIPT,
            )}
          />

          {receiptFiles.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Text type="secondary" style={{ fontSize: 16 }}>
                ใบเสร็จที่บันทึกไว้ ({receiptFiles.length} ไฟล์)
              </Text>
              <ImageGrid
                files={receiptFiles}
                loading={loading}
                token={token}
                onDelete={handleDeleteIMG}
                readOnly={roleId === 1 ? false : true}
              />
            </div>
          )}

          <Divider style={{ margin: "8px 0 16px" }} />

          {/* ── หมายเหตุ ── */}
          <Form.Item label="หมายเหตุ" name="memo">
            <TextArea
              rows={4}
              placeholder="กรอกหมายเหตุ (ถ้ามี)"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              paddingTop: 8,
            }}
          >
            <Button
              size="large"
              onClick={handleCancel}
              style={{ minWidth: 120, borderRadius: 8 }}
            >
              ยกเลิก
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              style={{ minWidth: 120, borderRadius: 8 }}
              disabled={dataDefault.dateCheckJudgeFn === 1}
            >
              บันทึก
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CloseContno;
